import { useState, useEffect, useMemo } from "react";

import { TextInput } from "@certa/catalyst/components/Inputs";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Search } from "@certa/icons/components/Search";
import { Checkbox, CheckboxGroup } from "@certa/catalyst/components/Checkbox";

import {
  TypographyVariants,
  Typography
} from "@certa/catalyst/components/Typography";
import { useFilterOverlay } from "../FilterOverlay/FilterOverlayContext";
import { Box } from "@certa/catalyst/layouts/Box";
import { useGetExtraJSON } from "@certa/queries/hooks/common.hook";
import { Spinner } from "@certa/icons/components/Spinner";
import { useIntl } from "react-intl";

const SelectFilter = () => {
  const intl = useIntl();
  const [facetSearchValue, setFacetSearchValue] = useState("");

  const {
    setButtonStates,
    label,
    activeColumn,
    searchValue = [],
    setSearchValue,
    isManualFiltering,
    filterProps
  } = useFilterOverlay();

  const { data: optionsFromApi, isLoading } = useGetExtraJSON(
    filterProps?.optionUrl ?? "",
    {
      enabled: !!filterProps?.optionUrl && !filterProps?.options?.length
    }
  );

  // Compute options based on propOptions or column values
  const options = useMemo(() => {
    if (filterProps?.options) return filterProps.options;
    if (optionsFromApi?.results) return optionsFromApi.results;

    if (!isManualFiltering) {
      return Array.from(
        new Set(activeColumn.getFacetedUniqueValues().keys())
      ).map(val => ({
        label: String(val),
        value: String(val)
      }));
    }

    return [];
  }, [filterProps, optionsFromApi, isManualFiltering, activeColumn]);

  const filteredData = useMemo(
    () =>
      options.filter(
        item =>
          item.label.toLowerCase().indexOf(facetSearchValue.toLowerCase()) !==
          -1
      ),
    [options, facetSearchValue]
  );

  // Calculate these values directly instead of using state and useEffect
  const isIndeterminate =
    searchValue.length > 0 && searchValue.length < options.length;
  const shouldCheckAll = searchValue.length === options.length;

  const onCheckAllChange = (isChecked: boolean) => {
    const selectedOptions = isChecked
      ? options.map(item => String(item.value))
      : [];
    setSearchValue(selectedOptions);
  };

  useEffect(() => {
    setButtonStates(filteredData.length === 0, searchValue.length === 0);
  }, [filteredData.length, searchValue.length, setButtonStates]);

  const noOptionsFound = intl.formatMessage({
    id: "common.noOptionsFound",
    defaultMessage: "No options found"
  });

  return (
    <Stack direction="vertical" gap="12" width="15.875rem">
      <TextInput
        label={label}
        placeholder={`Search By ${label}...`}
        value={facetSearchValue}
        leftIcon={<Search color="neutral-70" />}
        onChange={setFacetSearchValue}
        allowClear
        onClear={() => setFacetSearchValue("")}
        width="100%"
      />
      <Stack height="16rem" gap="12">
        {filteredData.length ? (
          <>
            <Checkbox
              indeterminate={isIndeterminate}
              onChange={onCheckAllChange}
              checked={shouldCheckAll}
              label="Select All"
            />
            <Box overflow="auto">
              <CheckboxGroup
                name={`select-filter-${label}`}
                value={searchValue}
                onChange={setSearchValue}
                direction="vertical"
              >
                {filteredData.map(item => (
                  <Checkbox
                    key={item.value}
                    value={String(item.value)}
                    label={item.label}
                  />
                ))}
              </CheckboxGroup>
            </Box>
          </>
        ) : (
          <Stack
            height="100%"
            direction="horizontal"
            justify="center"
            align="center"
          >
            {isLoading ? (
              <Spinner />
            ) : (
              <Typography variant={TypographyVariants.BODY}>
                {noOptionsFound}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export { SelectFilter };
